<template>
  <Transition name="btt">
    <button
      v-if="visible"
      type="button"
      class="back-to-top"
      aria-label="ページの先頭へ戻る"
      @click="scrollToTop"
    >
      <span class="back-to-top-label" aria-hidden="true">トップへ</span>
      <svg
        class="back-to-top-arrow"
        viewBox="0 0 512 512"
        width="18"
        height="18"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M233.4 105.4c12.5-12.5 32.8-12.5 45.3 0l192 192c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L256 173.3 86.6 342.6c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3l192-192z"
        />
      </svg>
    </button>
  </Transition>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";

// この量を超えてスクロールしたら表示する
const SHOW_AFTER_PX = 400;

const visible = ref(false);
let rafId = 0;

function update() {
  rafId = 0;
  visible.value = window.scrollY > SHOW_AFTER_PX;
}

// passive なスクロールリスナーを rAF で 1 フレームに間引く
function onScroll() {
  if (rafId) return;
  rafId = window.requestAnimationFrame(update);
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: prefersReducedMotion() ? "auto" : "smooth",
  });
}

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true });
  // ルーターの scrollBehavior でスクロール位置が変わった直後（復元やトップ戻し）にも
  // scroll イベントが発火するので、ルート遷移時のリセットは別途不要。初期値だけ即時計算する
  update();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
  if (rafId) window.cancelAnimationFrame(rafId);
});
</script>

<style scoped>
/* uiverse.io の back-to-top ボタン系パターン(MIT, vinodjangid07)をデザイントークンに合わせて移植。
   丸ボタン + 上矢印で、ホバー/フォーカス時に矢印が下からスライドインし、ラベルが現れる。
   元スニペットはラベルをボタンの下に出すが、画面下端に固定するため左側に出す。
   App.vue の全要素トランジションと干渉しないよう transition は各要素で明示する */
.back-to-top {
  position: fixed;
  right: 20px;
  bottom: 24px;
  /* v-app-bar(1005) より下、v-dialog のスクリム(2400)より下 */
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--c-accent);
  color: var(--c-accent-on);
  box-shadow: 0 4px 16px var(--c-shadow-btn);
  cursor: pointer;
  /* ページ遷移(View Transitions)のクロスフェードから切り離し、二重に見えないようにする */
  view-transition-name: back-to-top;
  transition:
    background-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;
}
.back-to-top:hover,
.back-to-top:focus-visible {
  background: var(--c-accent-hover);
  box-shadow: 0 8px 24px var(--c-shadow-btn);
  transform: translateY(-2px);
}
.back-to-top:focus-visible {
  outline: 2px solid var(--c-accent);
  outline-offset: 3px;
}
.back-to-top:active {
  transform: translateY(0);
  transition-duration: 0.08s;
}

.back-to-top-arrow {
  display: block;
}
.back-to-top:hover .back-to-top-arrow,
.back-to-top:focus-visible .back-to-top-arrow {
  animation: btt-slide-in-bottom 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}
@keyframes btt-slide-in-bottom {
  from {
    transform: translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* ラベルはボタン左側に。ホバー/フォーカスでフェードイン */
.back-to-top-label {
  position: absolute;
  right: calc(100% + 10px);
  top: 50%;
  transform: translate(6px, -50%);
  padding: 4px 10px;
  border-radius: 8px;
  background: var(--c-text-1);
  color: var(--c-bg);
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.back-to-top:hover .back-to-top-label,
.back-to-top:focus-visible .back-to-top-label {
  opacity: 1;
  transform: translate(0, -50%);
}

/* 出現/消滅（Transition name="btt"） */
.btt-enter-active,
.btt-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}
.btt-enter-from,
.btt-leave-to {
  opacity: 0;
  transform: translateY(12px);
}

@media (prefers-reduced-motion: reduce) {
  .back-to-top,
  .back-to-top-label,
  .btt-enter-active,
  .btt-leave-active {
    transition-duration: 0s;
  }
  .back-to-top:hover,
  .back-to-top:focus-visible,
  .back-to-top:active {
    transform: none;
  }
  .back-to-top:hover .back-to-top-arrow,
  .back-to-top:focus-visible .back-to-top-arrow {
    animation: none;
  }
  .btt-enter-from,
  .btt-leave-to {
    transform: none;
  }
}
</style>
